import {
  ActionIcon,
  Button,
  Checkbox,
  Flex,
  Group,
  Modal,
  Text,
  Tooltip,
} from "@mantine/core";
import { Select } from "src/components/shared/MantineCombobox";
import { IconInfoCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import { type ChartTypeRegistry } from "chart.js";
import { type Dispatch, type SetStateAction, useCallback } from "react";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  type IChart,
  type ILogbookCharts,
  setChartsData,
  type TChartValueSource,
} from "src/redux/slices/entriesGeneratorSlice";

import { isChartNumeric, resolveValueSource } from "../Charts/Chart.utils";

const NO_SORT_VALUE = "__none__";

const groupChartsByColumn = (charts: IChart[]) => {
  const groups: Array<{ sectionIndex: number; name: string; charts: IChart[] }> =
    [];
  charts.forEach(chart => {
    let group = groups.find(
      g => g.sectionIndex === chart.sectionIndex && g.name === chart.name,
    );
    if (!group) {
      group = { sectionIndex: chart.sectionIndex, name: chart.name, charts: [] };
      groups.push(group);
    }
    group.charts.push(chart);
  });
  return groups;
};

interface Props {
  opened: boolean;
  close: () => void;
  data: ILogbookCharts[];
  setData: Dispatch<SetStateAction<ILogbookCharts[]>>;
  includeUnverified: boolean;
  onIncludeUnverifiedChange: (checked: boolean) => void;
}

const ENTRIES_OPTION_VALUE = "__entries__";
const COUNT_OPTION_VALUE = "__count__";
const SUM_OPTION_PREFIX = "sum:";

const toOptionValue = (source: TChartValueSource) =>
  source.kind === "sum"
    ? `${SUM_OPTION_PREFIX}${source.column}`
    : source.kind === "count"
      ? COUNT_OPTION_VALUE
      : ENTRIES_OPTION_VALUE;

const fromOptionValue = (value: string | null): TChartValueSource =>
  value?.startsWith(SUM_OPTION_PREFIX)
    ? { kind: "sum", column: value.slice(SUM_OPTION_PREFIX.length) }
    : value === COUNT_OPTION_VALUE
      ? { kind: "count" }
      : { kind: "entries" };

const CreateChartModal = ({
  opened,
  close,
  data,
  setData,
  includeUnverified,
  onIncludeUnverifiedChange,
}: Props) => {
  const dispatch = useDispatch();
  const { logbooks } = useSelector(state => state.entriesGeneratorSlice);

  const updateChart = (id: string, updater: (chart: IChart) => IChart) => {
    setData(prev =>
      prev.map(log => ({
        ...log,
        data: log.data.map(chart => (chart.id === id ? updater(chart) : chart)),
      })),
    );
  };

  const handleSelect = (id: string, value: string) => {
    updateChart(id, chart => {
      const type = value as keyof ChartTypeRegistry;
      const next: IChart = { ...chart, type, trendline: false };
      if (
        type !== "pie" &&
        chart.valueSource?.kind !== "sum" &&
        !isChartNumeric(chart)
      ) {
        next.valueSource = { kind: "count" };
      }
      return next;
    });
  };

  const handleValueSource = (id: string, value: string | null) => {
    updateChart(id, chart => {
      const valueSource = fromOptionValue(value);
      return {
        ...chart,
        valueSource,
        trendline: chart.type !== "pie" ? chart.trendline : false,
      };
    });
  };

  const handleSortBy = (id: string, value: string | null) => {
    updateChart(id, chart => ({
      ...chart,
      sortBy: value && value !== NO_SORT_VALUE ? (value as "label" | "value") : undefined,
    }));
  };

  const handleSortDirection = (id: string, value: string | null) => {
    updateChart(id, chart => ({
      ...chart,
      sortDirection: value === "desc" ? "desc" : "asc",
    }));
  };

  const getValueSourceOptions = (chart: IChart, charts: IChart[]) => {
    const seen = new Set<string>();
    return [
      ...(chart.type !== "pie"
        ? [{ label: "Individual entries, in date order", value: ENTRIES_OPTION_VALUE }]
        : []),
      { label: `Count: number of entries per ${chart.name}`, value: COUNT_OPTION_VALUE },
      ...charts
        .filter(
          candidate =>
            candidate.name !== chart.name &&
            candidate.sectionIndex === chart.sectionIndex &&
            isChartNumeric(candidate),
        )
        .filter(candidate => {
          if (seen.has(candidate.name)) return false;
          seen.add(candidate.name);
          return true;
        })
        .map(candidate => ({
          label: `Sum: total ${candidate.name} per ${chart.name}`,
          value: `${SUM_OPTION_PREFIX}${candidate.name}`,
        })),
    ];
  };

  const handleCheckbox = (id: string, checked: boolean) => {
    updateChart(id, chart => ({ ...chart, trendline: checked }));
  };

  const handleAddChart = (lbid: string, sectionIndex: number, name: string) => {
    setData(prev =>
      prev.map(log => {
        if (log.lbid !== lbid) return log;
        const columnCharts = log.data
          .map((chart, i) => ({ chart, i }))
          .filter(
            ({ chart }) =>
              chart.sectionIndex === sectionIndex && chart.name === name,
          );
        const template = columnCharts[0]?.chart;
        if (!template) return log;

        const newChart: IChart = {
          ...template,
          id: crypto.randomUUID(),
          type: "pie",
          valueSource: { kind: "count" },
          trendline: false,
          sortBy: undefined,
          sortDirection: undefined,
          sumVals: undefined,
        };
        const insertAt = columnCharts[columnCharts.length - 1].i + 1;
        const nextData = [...log.data];
        nextData.splice(insertAt, 0, newChart);
        return { ...log, data: nextData };
      }),
    );
  };

  const handleRemoveChart = (id: string) => {
    setData(prev =>
      prev.map(log => ({ ...log, data: log.data.filter(c => c.id !== id) })),
    );
  };

  const handleCreate = () => {
    dispatch(
      setChartsData(
        data.map(log => ({
          ...log,
          data: log.data.map(chart => {
            const valueSource = resolveValueSource(chart);
            if (valueSource.kind !== "sum")
              return { ...chart, valueSource, sumVals: undefined };
            const source = log.data.find(
              candidate =>
                candidate.name === valueSource.column &&
                candidate.sectionIndex === chart.sectionIndex,
            );
            if (!source)
              return {
                ...chart,
                valueSource: { kind: "count" as const },
                sumVals: undefined,
              };
            return { ...chart, valueSource, sumVals: [...source.rawVals] };
          }),
        })),
      ),
    );
  };

  const renderModalTitle = useCallback(
    () => (
      <Flex gap={8}>
        <Text size="lg" fw="bold">
          Charts
        </Text>
        <Tooltip
          w={200}
          multiline
          transitionProps={{ duration: 200 }}
          label="Below you can set the charts for those fields which contained some values">
          <IconInfoCircle style={{ cursor: "pointer" }} />
        </Tooltip>
      </Flex>
    ),
    [],
  );

  return (
    <Modal size="100%" styles={{ content: { maxWidth: 1200 }, body: { minWidth: 800 } }} title={renderModalTitle()} opened={opened} onClose={close}>
      <Checkbox
        mb={16}
        checked={includeUnverified}
        onChange={event =>
          onIncludeUnverifiedChange(event.currentTarget.checked)
        }
        label="Include unverified entries (Open, Re-Verify)"
      />
      {data.length === 0 && (
        <Text>Provided fields do not contain any values</Text>
      )}
      {data.length > 0 &&
        data.map((el: ILogbookCharts) => (
          <div key={el.lbid}>
            <Text mb={12}>
              {logbooks.find(log => log.value === el.lbid)?.label}
            </Text>
            <div style={{ marginLeft: 20 }}>
              {groupChartsByColumn(el.data).map(group => (
                <div
                  key={`${group.sectionIndex}-${group.name}`}
                  style={{ marginBottom: 20 }}>
                  <Flex align="center" gap={4} mb={8}>
                    <Text>{group.name}</Text>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      aria-label={`Add another chart for ${group.name}`}
                      onClick={() =>
                        handleAddChart(el.lbid, group.sectionIndex, group.name)
                      }>
                      <IconPlus size={14} />
                    </ActionIcon>
                  </Flex>
                  {group.charts.map(chart => {
                    const valueSource = resolveValueSource(chart);
                    return (
                      <Flex
                        mb={12}
                        align="flex-end"
                        gap={12}
                        justify="space-between"
                        key={chart.id}>
                        <Flex align="flex-end" gap={12}>
                          <Select
                            label="Chart type"
                            w={100}
                            withinPortal
                            data={[
                              { label: "Line", value: "line" },
                              { label: "Bar", value: "bar" },
                              { label: "Pie", value: "pie" },
                            ]}
                            value={chart.type}
                            onChange={(value: string) =>
                              handleSelect(chart.id, value)
                            }
                          />
                          <Select
                            label="Value"
                            w={320}
                            comboboxProps={{ withinPortal: true }}
                            allowDeselect={false}
                            data={getValueSourceOptions(chart, el.data)}
                            value={toOptionValue(valueSource)}
                            onChange={(value: string | null) =>
                              handleValueSource(chart.id, value)
                            }
                          />
                          {valueSource.kind !== "entries" && (
                            <>
                              <Select
                                label="Sort by"
                                w={160}
                                comboboxProps={{ withinPortal: true }}
                                allowDeselect={false}
                                data={[
                                  { label: "Original order", value: NO_SORT_VALUE },
                                  { label: "Category", value: "label" },
                                  { label: "Value", value: "value" },
                                ]}
                                value={chart.sortBy ?? NO_SORT_VALUE}
                                onChange={(value: string | null) =>
                                  handleSortBy(chart.id, value)
                                }
                              />
                              {chart.sortBy && (
                                <Select
                                  label="Direction"
                                  w={140}
                                  comboboxProps={{ withinPortal: true }}
                                  allowDeselect={false}
                                  data={[
                                    { label: "Ascending", value: "asc" },
                                    { label: "Descending", value: "desc" },
                                  ]}
                                  value={chart.sortDirection ?? "asc"}
                                  onChange={(value: string | null) =>
                                    handleSortDirection(chart.id, value)
                                  }
                                />
                              )}
                            </>
                          )}
                        </Flex>
                        <Flex align="center" gap={12}>
                          <Checkbox
                            disabled={chart.type === "pie"}
                            checked={chart.trendline}
                            onChange={event =>
                              handleCheckbox(chart.id, event.currentTarget.checked)
                            }
                            label="Trendline"
                          />
                          <ActionIcon
                            size="lg"
                            variant="subtle"
                            color="red"
                            aria-label={`Remove this chart for ${group.name}`}
                            onClick={() => handleRemoveChart(chart.id)}>
                            <IconTrash size={20} />
                          </ActionIcon>
                        </Flex>
                      </Flex>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ))}
      <Group w="100%" bg="var(--mantine-color-body)" style={{ position: "sticky", bottom: 0, paddingBottom: 16, marginBottom: -16, zIndex: 1 }} justify="right" mt={10}>
        <Button onClick={handleCreate} disabled={data.length === 0}>
          Create
        </Button>
      </Group>
    </Modal>
  );
};

export default CreateChartModal;
